import classnames from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import shortAndSweet from 'short-and-sweet'
import styles from '../gallery_editor/gallery_editor.module.css'
import { GalleryEditorProps, GalleryImageList, ItemProps, UploaderProps, UploadingFileProps } from './types'
import { Button, JSONFetcher, LogoSpinner, Sortable, handleError, themeColor, useTextareaAutosize } from 'src/shared'
import { Dropzone } from 'src/shared/dropzone'
export const GalleryEditor = ({ indexUrl }: GalleryEditorProps) => {
  const [images, setImages] = useState<GalleryImageList>([])

  useEffect(() => {
    JSONFetcher.call<unknown, GalleryImageList>({ url: indexUrl }).then(setImages).catch(handleError)
  }, [indexUrl])

  return (
    <>
      <Uploader url={indexUrl} onUpload={setImages} />
      {images?.length > 0 && <Sortable items={images} setItems={setImages} ItemComponent={Item} />}
    </>
  )
}

const Uploader = (props: UploaderProps): JSX.Element => {
  const [uploadingFiles, setUploadingFiles] = useState<Array<File>>([])

  const handleUpload = useCallback((acceptedFiles: File[]) => {
    setUploadingFiles((old) => old.concat(acceptedFiles))
  }, [])

  const dequeue = useCallback(() => {
    setUploadingFiles((old) => old.slice(1))
  }, [])

  const handleFileComplete = useCallback(
    (data: GalleryImageList) => {
      props.onUpload(data)
      dequeue()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onUpload, dequeue]
  )

  return (
    <div className={styles.uploadContainer}>
      <Dropzone
        accept={['image/jpeg', 'image/png']}
        multiple={true}
        maxSize={1024 * 1024 * 30}
        handleUpload={handleUpload}
      />

      <p className={styles.hint}>{i18n.t('modules.gallery_editor.help_text')}</p>

      {uploadingFiles.map((f, i) => (
        <UploadingFile
          file={f}
          {...props}
          key={f.name}
          onError={dequeue}
          onUpload={handleFileComplete}
          startLoading={i === 0}
        />
      ))}
    </div>
  )
}

const UploadingFile = ({ url, file, onUpload, onError, startLoading }: UploadingFileProps): JSX.Element | null => {
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    if (!startLoading) return
    const body = new FormData()
    body.append('file', file)

    JSONFetcher.call<FormData, GalleryImageList>({
      url,
      method: 'post',
      body,
      config: {
        withCredentials: true,
        headers: {
          'Content-Type': undefined,
        },
      },
    })
      .then((data) => {
        onUpload(data)
        setLoading(false)
      })
      .catch((error) => {
        handleError(error)
        onError()
        setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startLoading])

  if (!loading && !startLoading) return null

  return (
    <div className={styles.loadingContainer}>
      <div className={styles.itemContainer}>
        <div className={styles.itemImageWrapper}>
          <LogoSpinner smallSpinner color={themeColor('green-500')} />
        </div>
        <div className={styles.itemDetails}>{file.name}</div>
      </div>
    </div>
  )
}

const Item = ({ id, onDelete, description: initialDescription, updateUrl, thumb }: ItemProps): JSX.Element => {
  const [description, setDescription] = useState(initialDescription || '')
  const [loading, setLoading] = useState(false)
  const [dirty, setDirty] = useState(false)
  const textarea = useRef<HTMLTextAreaElement>(null)

  useTextareaAutosize(textarea.current)

  useEffect(() => {
    shortAndSweet(`#textarea-image-${id}`, {
      counterClassName: 'module-text-counter',
      counterLabel: '{length} / {maxlength}',
      append: (el, counter) => {
        el.parentNode?.insertBefore(counter, el.nextSibling)
      },
    })
  }, [id])

  const submitDescription = useCallback(() => {
    setLoading(true)
    JSONFetcher.put({
      url: updateUrl,
      body: { description },
      success: () => {
        setLoading(false)
        setDirty(false)
      },
    })
  }, [updateUrl, description])

  const deleteImage = useCallback(() => {
    if (window.confirm(i18n.t('modules.gallery_editor.confirm_delete'))) {
      JSONFetcher.delete({
        url: updateUrl,
        success: (data) => onDelete(data),
      })
    }
  }, [onDelete, updateUrl])

  /** @type {React.FormEventHandler<HTMLTextAreaElement>} */
  const onDescriptionChanged: React.FormEventHandler<HTMLTextAreaElement> = useCallback((e) => {
    setDescription((e.target as HTMLTextAreaElement).value)
    setDirty(true)
  }, [])

  return (
    <>
      <div className={styles.itemContainer}>
        <div className={styles.itemImageWrapper}>
          <img src={thumb} alt="" />
        </div>
        <div className={styles.itemDetails}>
          <label>{i18n.t('modules.gallery_editor.item_description')}</label>
          <textarea
            id={`textarea-image-${id}`}
            rows={2}
            className="form-control"
            maxLength={250}
            value={description}
            onChange={onDescriptionChanged}
            onKeyDown={(event: React.KeyboardEvent) => event.stopPropagation()} //Ignore Sortable's event handlers
            ref={textarea}
          />
        </div>

        <div className={styles.itemControls}>
          <Button
            loading={loading}
            disabled={!dirty}
            onClick={submitDescription}
            className={classnames('btn-primary', styles.submitButton)}
          >
            {i18n.t('modules.gallery_editor.item_save')}
          </Button>
          <br />
          <Button onClick={deleteImage} className="btn-danger">
            {i18n.t('modules.gallery_editor.item_delete')}
          </Button>
        </div>
      </div>
    </>
  )
}
